/**
 * Wave/agent presentation model for the Redesign v3 Monitor (UI_SDD §R.3).
 *
 * Rosters come from WAVE_AGENTS — never hardcoded — so the 12th agent (D11)
 * and any future roster change flow through automatically.
 */
import type { AgentState } from '../../stores/scanStore'
import { AGENT_DESCRIPTIONS, WAVE_AGENTS } from '../../types/api'

export type AgentStatusKey =
  | 'waiting'
  | 'running'
  | 'complete'
  | 'warned'
  | 'failed'
  | 'skipped'
  | 'done_with_problems'

/**
 * Status colours as THEME TOKENS rather than hex (DEC-130).
 *
 * The token name is exported alongside the resolved colour because two call
 * sites need the name itself: one derives a translucent border from the same
 * hue, and a hex string cannot carry an alpha without string surgery that
 * `var()` would break.
 */
export const STATUS_TOKEN: Record<AgentStatusKey, string> = {
  waiting: 'faint',
  running: 'accent-text',
  complete: 'success',
  warned: 'warn',
  failed: 'critical',
  skipped: 'faint',
  // DEC-197: amber is a status hue, never a severity (DEC-068).
  done_with_problems: 'warn',
}

/**
 * The same statuses as marks: dots, wave bands, progress fills (DEC-131).
 * Separate from STATUS_TOKEN because a mark is a graphical object at 3:1 while
 * the status label beside it is 8px text at 4.5:1, and on a light ground no
 * single value satisfies both. Identical to STATUS_TOKEN's values on dark.
 */
export const STATUS_MARK_TOKEN: Record<AgentStatusKey, string> = {
  waiting: 'mark-idle',
  running: 'mark-running',
  complete: 'mark-success',
  warned: 'mark-warn',
  failed: 'mark-critical',
  skipped: 'mark-idle',
  done_with_problems: 'mark-warn',
}

/** `rgb(var(--token))`, or with an alpha when one is given. */
export function tokenColor(token: string, alpha?: number): string {
  return alpha === undefined ? `rgb(var(--${token}))` : `rgb(var(--${token}) / ${alpha})`
}

/** Text colours, for the status labels. */
export const STATUS_COLOR: Record<AgentStatusKey, string> = Object.fromEntries(
  Object.entries(STATUS_TOKEN).map(([k, t]) => [k, tokenColor(t)]),
) as Record<AgentStatusKey, string>

/** Mark colours, for dots, bands and fills. */
export const STATUS_MARK: Record<AgentStatusKey, string> = Object.fromEntries(
  Object.entries(STATUS_MARK_TOKEN).map(([k, t]) => [k, tokenColor(t)]),
) as Record<AgentStatusKey, string>

export const STATUS_LABEL: Record<AgentStatusKey, string> = {
  waiting: 'Waiting',
  running: 'Running',
  complete: 'Complete',
  warned: 'Warned',
  failed: 'Failed',
  skipped: 'Skipped',
  done_with_problems: 'Done with problems',
}

// DEC-093 — a running wave/agent frozen by an LLM-unavailable pause reads as
// amber (status), never a severity color (DEC-068).
export const PAUSED_TOKEN = 'warn'
export const PAUSED_MARK_TOKEN = 'mark-warn'
export const PAUSED_COLOR = tokenColor(PAUSED_TOKEN)
export const PAUSED_MARK = tokenColor(PAUSED_MARK_TOKEN)
export const PAUSED_TINT = tokenColor(PAUSED_TOKEN, 0.12)
export const PAUSED_BORDER = tokenColor(PAUSED_TOKEN, 0.4)

// DEC-094 — Tier-2 round-decision pending. Deliberately distinct from
// PAUSED_COLOR: a scan-wide LLM-unavailable pause (amber) and a per-agent
// "waiting on you" decision are different conditions and must read differently.
export const DECISION_TOKEN = 'decision'
export const DECISION_MARK_TOKEN = 'mark-decision'
export const DECISION_COLOR = tokenColor(DECISION_TOKEN)
export const DECISION_MARK = tokenColor(DECISION_MARK_TOKEN)
export const DECISION_TINT = tokenColor(DECISION_TOKEN, 0.12)
export const DECISION_BORDER = tokenColor(DECISION_TOKEN, 0.4)

/** The token behind `displayColor`, for callers that need to derive an alpha. */
export function displayToken(status: AgentStatusKey, isPaused: boolean): string {
  return status === 'running' && isPaused ? PAUSED_TOKEN : STATUS_TOKEN[status]
}

/** Mark equivalent of `displayToken`. */
export function displayMarkToken(status: AgentStatusKey, isPaused: boolean): string {
  return status === 'running' && isPaused ? PAUSED_MARK_TOKEN : STATUS_MARK_TOKEN[status]
}

export function displayMark(status: AgentStatusKey, isPaused: boolean): string {
  return tokenColor(displayMarkToken(status, isPaused))
}

export function displayColor(status: AgentStatusKey, isPaused: boolean): string {
  return status === 'running' && isPaused ? PAUSED_COLOR : STATUS_COLOR[status]
}

export function displayLabel(status: AgentStatusKey, isPaused: boolean): string {
  return status === 'running' && isPaused ? 'Paused' : STATUS_LABEL[status]
}

const DONE: AgentStatusKey[] = ['complete', 'warned', 'failed', 'skipped', 'done_with_problems']

/** Finished and kept its findings: complete, warned (a tool was missing) or done with
 *  problems (DEC-197). */
export function finishedOk(status: string): boolean {
  return status === 'complete' || status === 'warned' || status === 'done_with_problems'
}

/** DEC-197: the short status a wave row shows. A timeout reads as such, not "Failed". */
export function agentStatusWord(a: Pick<AgentState, 'status' | 'failure'>, isPaused = false): string {
  if (a.status === 'failed' && a.failure?.kind === 'timeout') return 'Timed out'
  return displayLabel(a.status as AgentStatusKey, isPaused)
}

/** DEC-197: rows that open the agent pop-up (the reason is one click away, never inline). */
export function hasStory(a: Pick<AgentState, 'status'>): boolean {
  return a.status === 'failed' || a.status === 'done_with_problems' || a.status === 'warned' || a.status === 'skipped'
}

export function isDone(status: string): boolean {
  return DONE.includes(status as AgentStatusKey)
}

export interface WaveModel {
  num: number
  tag: string
  agents: AgentState[]
  status: 'waiting' | 'running' | 'done'
  /** "3/3 done · 12 findings" */
  summary: string
  doneCount: number
  agentCount: number
  /** true only for a wave that was running when the scan-wide pause landed */
  paused: boolean
}

const WAVE_TAG: Record<number, string> = {
  1: 'Parallel',
  2: 'Parallel',
  3: 'Sequential',
  4: 'Final pass',
}

export function buildWaves(agents: Record<string, AgentState>, isPaused = false): WaveModel[] {
  return ([1, 2, 3, 4] as const).map((num) => {
    const ids = WAVE_AGENTS[num] ?? []
    const list = ids.map((id) => agents[id]).filter(Boolean)
    const nonSkipped = list.filter((a) => a.status !== 'skipped')
    const doneCount = nonSkipped.filter((a) => isDone(a.status)).length
    const anyStarted = list.some((a) => a.status !== 'waiting' && a.status !== 'skipped')
    const allDone = nonSkipped.length === 0 || nonSkipped.every((a) => isDone(a.status))

    const findings = list.reduce((s, a) => s + a.findings_count, 0)
    const status: WaveModel['status'] = allDone && anyStarted ? 'done' : anyStarted ? 'running' : nonSkipped.length === 0 ? 'done' : 'waiting'
    const paused = status === 'running' && isPaused

    return {
      num,
      tag: WAVE_TAG[num],
      agents: list,
      status,
      summary: `${doneCount}/${nonSkipped.length || 0} done · ${findings} finding${findings === 1 ? '' : 's'}${paused ? ' · paused' : ''}`,
      doneCount,
      agentCount: nonSkipped.length,
      paused,
    }
  })
}

export function agentDescription(agentId: string): string {
  return AGENT_DESCRIPTIONS[agentId] ?? ''
}

// ---------------------------------------------------------------------------
// Wave-3 inter-agent context (DEC-055; honesty rule per §R.3)
// ---------------------------------------------------------------------------

export interface ContextBadge {
  /** true: the service map reached D7 (recorded); false: D7 ran without it */
  carried: boolean
  text: string
}

/**
 * DEC-188 decision 5. The only wave-3 hand-off that carries data is D12 -> D7
 * (the service map). There is no D4 -> D12 edge, so D12 never gets a badge.
 *
 * The D7 badge reads D7's RECORDED inputs, never D12's finding count: the map
 * passes with zero Coupling findings, and is unused when D7 took no AI step.
 * No badge until D7 finished, and none when its inputs were not recorded.
 */
export function contextBadgeFor(
  agentId: string,
  agents: Record<string, AgentState>,
): ContextBadge | null {
  if (agentId !== 'd7_performance') return null
  const d7 = agents.d7_performance
  if (!d7 || !isDone(d7.status) || d7.status === 'skipped') return null
  const inputs = d7.inputs
  if (!inputs) return null
  const upstreamLabel = agents.d12_coupling?.label ?? 'Coupling'
  if (inputs.coupling_graph === true) return { carried: true, text: `Service map from ${upstreamLabel}` }
  return { carried: false, text: `No service map from ${upstreamLabel}` }
}
